import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "./TimePicker.css";
import { Button, IconButton, Tooltip } from "@mui/material";
import { CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";
import { ReactComponent as Info } from "../../../assets/images/info.svg";

const MINUTES_STEP = 15;

const getToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const isSameDay = (date1, date2) => {
  return (
    date1 &&
    date2 &&
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

const getHourMinute = (timeStr) => {
  if (!timeStr) return [null, null];
  const [hr, min] = timeStr.split(":").map(Number);
  return [hr, min];
};

const getHourOptions = (type, minStartHour, startDate, endTime, maxTime) => {
  let maxHour = type === "startTime" ? 23 : 24;
  let minHour = 0;

  // Apply maxTime restriction if provided
  if (maxTime) {
    const [maxHr] = getHourMinute(maxTime);
    if (maxHr !== null) {
      maxHour = Math.min(maxHour, maxHr);
    }
  }

  if (
    type === "startTime" &&
    minStartHour !== null &&
    startDate &&
    isSameDay(new Date(startDate), getToday())
  ) {
    minHour = minStartHour;
  }
  if (endTime) {
    const [maxHr] = getHourMinute(endTime);
    maxHour = Math.min(maxHour, maxHr);
  }
  return Array.from({ length: maxHour - minHour + 1 }, (_, i) =>
    (i + minHour).toString().padStart(2, "0"),
  );
};

const getMinuteOptions = ({
  hour,
  type,
  minStartHour,
  minStartMinute,
  startDate,
  startHour,
  startMinute,
  endTime,
  disabled = false,
  maxTime,
}) => {
  let minMinute = 0;
  let maxMinute = 59;

  if (
    type === "startTime" &&
    minStartHour !== null &&
    startDate &&
    isSameDay(new Date(startDate), getToday()) &&
    hour === minStartHour.toString().padStart(2, "0")
  ) {
    minMinute = minStartMinute;
  }

  if (
    type === "endTime" &&
    startHour !== null &&
    hour === startHour.toString().padStart(2, "0")
  ) {
    minMinute = Math.ceil((startMinute + 1) / MINUTES_STEP) * MINUTES_STEP;
  }

  if (endTime) {
    const [maxHr, maxMin] = getHourMinute(endTime);
    if (hour === maxHr.toString().padStart(2, "0")) {
      maxMinute = maxMin;
    }
  }

  // Apply maxTime restriction for minutes
  if (maxTime) {
    const [maxHr, maxMin] = getHourMinute(maxTime);
    if (maxHr !== null && hour === maxHr.toString().padStart(2, "0")) {
      maxMinute = Math.min(maxMinute, maxMin);
    }
  }

  let minutes = Array.from({ length: 60 }, (_, i) => i)
    .filter((i) => i % MINUTES_STEP === 0 && i >= minMinute && i <= maxMinute)
    .map((i) => i.toString().padStart(2, "0"));

  if (hour === "24") {
    minutes = ["00"];
  }

  return minutes;
};

const TimeInput = ({
  heading,
  value,
  onChange,
  height,
  rtcButton,
  handleRTC,
  type,
  startTime,
  endTime,
  startDate,
  error,
  minHr,
  noTooltip,
  disabled = false,
  isRtc = false,
  style,
  maxTime,
}) => {
  const [open, setOpen] = useState(false);
  const clockList = useRef(null);
  const inputRef = useRef(null);
  const [hour, setHour] = useState("");
  const [minute, setMinute] = useState("");
  const [rtcCheck, setRtcCheck] = useState("");
  const [popupStyle, setPopupStyle] = useState({});

  useEffect(() => {
    setRtcCheck(isRtc);
  }, [isRtc]);

  useEffect(() => {
    if (value) {
      const [hr, min] = value.split(":");
      setHour(hr || "");
      setMinute(min || "");
    } else {
      setHour("");
      setMinute("");
    }
  }, [value]);

  useEffect(() => {
    const closeOpenMenus = (e) => {
      if (clockList.current && open && !clockList.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOpenMenus);
    return () => document.removeEventListener("mousedown", closeOpenMenus);
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const popupHeight = 230;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      const openAbove = spaceBelow < popupHeight && spaceAbove > spaceBelow;

      setPopupStyle({
        position: "fixed",
        top: openAbove ? rect.top - popupHeight - 4 : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 9999,
      });
    }
  }, [open]);

  let minStartHour = null;
  let minStartMinute = null;
  if (type && minHr) {
    const now = new Date();
    now.setHours(now.getHours() + (minHr || 3));
    minStartHour = now.getHours();
    minStartMinute = now.getMinutes();
    if (minStartMinute % MINUTES_STEP !== 0) {
      minStartMinute = Math.ceil(minStartMinute / MINUTES_STEP) * MINUTES_STEP;
      if (minStartMinute === 60) {
        minStartMinute = 0;
        minStartHour += 1;
      }
    }
  }

  let startHour = null;
  let startMinute = null;
  if (type === "endTime" && startTime) {
    [startHour, startMinute] = getHourMinute(startTime);
  }

  let hours = getHourOptions(type, minStartHour, startDate, endTime, maxTime);
  if (type === "endTime" && startHour !== null && !isNaN(startHour)) {
    hours = hours.filter(
      (hr) => parseInt(hr, 10) > startHour || parseInt(hr, 10) === startHour,
    );
  }

  let minutes = getMinuteOptions({
    hour,
    type,
    minStartHour,
    minStartMinute,
    startDate,
    startHour,
    startMinute,
    endTime,
    maxTime,
  });

  if (
    (type === "startTime" &&
      minStartHour !== null &&
      startDate &&
      isSameDay(new Date(startDate), getToday()) &&
      hour === minStartHour?.toString().padStart(2, "0") &&
      minutes.length === 0) ||
    (type === "endTime" &&
      startHour !== null &&
      hour === startHour?.toString().padStart(2, "0") &&
      minutes.length === 0)
  ) {
    hours = hours.filter(
      (hr) =>
        parseInt(hr, 10) > (type === "startTime" ? minStartHour : startHour),
    );
    if (hours.length > 0) {
      minutes = getMinuteOptions({
        hour: hours[0],
        type,
        minStartHour,
        minStartMinute,
        startDate,
        startHour,
        startMinute,
        endTime,
        maxTime,
      });
    }
  }

  const handleHourInput = (hr) => {
    setHour(hr);
    if (minute) {
      onChange(`${hr}:${minute}`);
      setOpen(false);
    }
  };

  const handleMinuteInput = (min) => {
    setMinute(min);
    if (hour) {
      onChange(`${hour}:${min}`);
      setOpen(false);
    }
  };

  const handleRTCCheck = () => {
    if (rtcCheck) {
      setRtcCheck(false);
      handleRTC("");
    } else {
      setRtcCheck(true);
      handleRTC("24:00");
    }
  };

  return (
    <div ref={inputRef} className="time-picker-wrapper">
      <div className="display_flex_row_gap_8_only">
        <div className="contentHeading">
          {heading}{" "}
          {type === "startTime" && !noTooltip && (
            <div style={{ position: "relative" }}>
              <Tooltip
                style={{ position: "absolute", top: "-24px", left: "75px" }}
                title="The start time must be at least 3 hours later than the current time."
              >
                <IconButton>
                  <Info
                    style={{ fontSize: 16, color: "#666", marginTop: "1.5px" }}
                  />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </div>
        {rtcButton && (
          <div
            className={`display_flex_row_gap_8 anotherClass`}
            onClick={handleRTCCheck}
          >
            <span
              className="contentHeading"
              style={{
                color: "var(--secondary-color-text)",
                fontWeight: "500",
              }}
            >
              RTC
            </span>
            {rtcCheck ? (
              <CheckBox
                style={{
                  fontSize: "12px",
                  paddingRight: "40%",
                  color: "var(--secondary-color-text)",
                }}
              />
            ) : (
              <CheckBoxOutlineBlank
                style={{ fontSize: "12px", paddingRight: "40%" }}
              />
            )}
          </div>
        )}
      </div>

      {/* Input Box with Editable Fields */}
      <div
        className="input_select_cont"
        style={{
          ...style,
          height: `${height || "45"}px`,
          backgroundColor: disabled || rtcCheck ? "#e9e9e9" : "",
          cursor: disabled || rtcCheck ? "not-allowed" : "pointer",
        }}
        onClick={() => {
          if (!disabled && !rtcCheck) {
            setOpen(!open);
          }
        }}
      >
        <div className="display_flex_row_center timeInputValue">
          <input
            type="text"
            maxLength={2}
            value={hour}
            disabled={disabled || rtcCheck}
            placeholder="HH"
            className="time-input"
            onChange={(e) => {
              const val = e.target.value;
              if (/^\d{0,2}$/.test(val) && (val === "" || Number(val) <= 24)) {
                setHour(val);

                if (minute) onChange(`${val}:${minute}`);
              }
            }}
          />
          <div className="display_flex_row_center separator">:</div>
          <input
            type="text"
            maxLength={2}
            value={minute}
            disabled={disabled || rtcCheck}
            placeholder="MM"
            className="time-input"
            onChange={(e) => {
              const val = e.target.value;
              const validOptions = ["00", "15", "30", "45"];
              if (
                val === "" ||
                validOptions.some((opt) => opt.startsWith(val))
              ) {
                setMinute(val);
                if (hour) onChange(`${hour}:${val}`);
              }
            }}
            onBlur={() => {
              const validOptions = ["00", "15", "30", "45"];
              if (!minute) return;
              if (validOptions.includes(minute)) return;

              const numVal = Number(minute);
              let nearest = validOptions.reduce((a, b) =>
                Math.abs(Number(a) - numVal) < Math.abs(Number(b) - numVal)
                  ? a
                  : b,
              );
              setMinute(nearest);
              if (hour) onChange(`${hour}:${nearest}`);
            }}
          />
        </div>
      </div>

      {error && <div className="dateErrorHeading">{error}</div>}

      {open &&
        ReactDOM.createPortal(
          <div
            className="pop-up-body"
            style={popupStyle}
            ref={clockList}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex" }}>
              <div className="hr-list">
                {hours.map((hr) => (
                  <div
                    key={hr}
                    onClick={() => handleHourInput(hr)}
                    className={`list-item ${hr === hour ? "selected" : ""}`}
                  >
                    {hr}
                  </div>
                ))}
              </div>
              <div className="hr-list">
                {minutes.map((min) => (
                  <div
                    key={min}
                    onClick={() => handleMinuteInput(min)}
                    className={`list-item ${min === minute ? "selected" : ""}`}
                  >
                    {min}
                  </div>
                ))}
              </div>
            </div>
            <div className="clock-footer">
              <Button variant="text" onClick={() => setOpen(false)}>
                OK
              </Button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default TimeInput;
